// VietQR (NAPAS 247) transfer codes, generated locally — no service is called.
//
// The QR is just a text string. The banking app scans it, reads the bank code
// (BIN) + account number, then looks the account holder up through NAPAS itself
// and shows the real name before the payer confirms. So:
//  - BIN + account number is enough for a valid code; the holder's name is NOT
//    part of it.
//  - There is no signature or secret — anyone who knows the format can build
//    one, and the scanner always sees the name the bank returns, not the name
//    printed next to the code.
//
// The string follows the EMVCo MPM layout: a run of [2-digit id][2-digit
// length][value] fields, closed by a CRC16 over everything before it.

import qrcode from 'qrcode-generator';
import { BankInfo } from '../types';

/**
 * NAPAS-issued BIN per bank, in the order the picker lists them: the banks
 * people here actually transfer with first, then the rest alphabetically.
 *
 * `name` is the short name printed on the QR card, `fullName` the legal name
 * shown under it so "MB" and "MSB" cannot be mistaken for each other while
 * picking. `aliases` stay because bank names saved before the picker existed
 * are free text the user typed ("TP Bank", "TPBank", "Tiên Phong"...), and a
 * saved profile still has to resolve to a BIN.
 */
const BANKS: { bin: string; name: string; fullName: string; aliases: string[] }[] = [
    { bin: '970436', name: 'Vietcombank', fullName: 'NH TMCP Ngoại Thương Việt Nam', aliases: ['vietcombank', 'vcb', 'ngoaithuong'] },
    { bin: '970422', name: 'MB', fullName: 'NH TMCP Quân Đội', aliases: ['mb', 'mbbank', 'quandoi'] },
    { bin: '970407', name: 'Techcombank', fullName: 'NH TMCP Kỹ Thương Việt Nam', aliases: ['techcombank', 'tcb', 'kythuong'] },
    { bin: '970415', name: 'VietinBank', fullName: 'NH TMCP Công Thương Việt Nam', aliases: ['vietinbank', 'ctg', 'congthuong'] },
    { bin: '970418', name: 'BIDV', fullName: 'NH TMCP Đầu Tư và Phát Triển Việt Nam', aliases: ['bidv', 'dautuvaphattrien'] },
    { bin: '970405', name: 'Agribank', fullName: 'NH NN & PTNT Việt Nam', aliases: ['agribank', 'agr', 'nongnghiep'] },
    { bin: '970423', name: 'TPBank', fullName: 'NH TMCP Tiên Phong', aliases: ['tpbank', 'tpb', 'tienphong', 'tienphongbank'] },
    { bin: '970432', name: 'VPBank', fullName: 'NH TMCP Việt Nam Thịnh Vượng', aliases: ['vpbank', 'vpb', 'vietnamthinhvuong'] },
    { bin: '970416', name: 'ACB', fullName: 'NH TMCP Á Châu', aliases: ['acb', 'achau'] },
    { bin: '970403', name: 'Sacombank', fullName: 'NH TMCP Sài Gòn Thương Tín', aliases: ['sacombank', 'stb', 'saigonthuongtin'] },

    { bin: '970425', name: 'ABBANK', fullName: 'NH TMCP An Bình', aliases: ['abbank', 'anbinh'] },
    { bin: '970409', name: 'BacABank', fullName: 'NH TMCP Bắc Á', aliases: ['bacabank', 'baca', 'bacabankvn'] },
    { bin: '970438', name: 'BaoVietBank', fullName: 'NH TMCP Bảo Việt', aliases: ['baovietbank', 'baoviet', 'bvb'] },
    { bin: '970454', name: 'BVBank', fullName: 'NH TMCP Bản Việt', aliases: ['bvbank', 'vietcapitalbank', 'banvietbank', 'banviet'] },
    { bin: '546034', name: 'CAKE', fullName: 'CAKE by VPBank', aliases: ['cake', 'cakebyvpbank'] },
    { bin: '970444', name: 'CBBank', fullName: 'NH Thương Mại TNHH MTV Xây Dựng Việt Nam', aliases: ['cbbank', 'cb', 'xaydung'] },
    { bin: '422589', name: 'CIMB', fullName: 'NH TNHH MTV CIMB Việt Nam', aliases: ['cimb', 'cimbbank'] },
    { bin: '970446', name: 'COOPBANK', fullName: 'NH Hợp Tác Xã Việt Nam', aliases: ['coopbank', 'hoptacxa'] },
    { bin: '970406', name: 'DongABank', fullName: 'NH TMCP Đông Á', aliases: ['dongabank', 'donga', 'dab'] },
    { bin: '970431', name: 'Eximbank', fullName: 'NH TMCP Xuất Nhập Khẩu Việt Nam', aliases: ['eximbank', 'eib', 'xuatnhapkhau'] },
    { bin: '970408', name: 'GPBank', fullName: 'NH Thương Mại TNHH MTV Dầu Khí Toàn Cầu', aliases: ['gpbank', 'gpb', 'daukhitoancau'] },
    { bin: '970437', name: 'HDBank', fullName: 'NH TMCP Phát Triển TP. Hồ Chí Minh', aliases: ['hdbank', 'hdb'] },
    { bin: '970442', name: 'Hong Leong', fullName: 'NH TNHH MTV Hong Leong Việt Nam', aliases: ['hongleong', 'hongleongbank', 'hlbvn'] },
    { bin: '458761', name: 'HSBC', fullName: 'NH TNHH MTV HSBC Việt Nam', aliases: ['hsbc', 'hsbcvietnam'] },
    { bin: '970434', name: 'Indovina', fullName: 'NH TNHH Indovina', aliases: ['indovina', 'ivb', 'indovinabank'] },
    { bin: '668888', name: 'KBank', fullName: 'NH Đại Chúng TNHH Kasikornbank', aliases: ['kbank', 'kasikornbank'] },
    { bin: '970452', name: 'KienLongBank', fullName: 'NH TMCP Kiên Long', aliases: ['kienlongbank', 'kienlong', 'klb'] },
    { bin: '970449', name: 'LPBank', fullName: 'NH TMCP Lộc Phát Việt Nam', aliases: ['lpbank', 'lienvietpostbank', 'lienviet', 'locphat'] },
    { bin: '970426', name: 'MSB', fullName: 'NH TMCP Hàng Hải Việt Nam', aliases: ['msb', 'maritimebank', 'hanghai'] },
    { bin: '970428', name: 'NamABank', fullName: 'NH TMCP Nam Á', aliases: ['namabank', 'nama'] },
    { bin: '970419', name: 'NCB', fullName: 'NH TMCP Quốc Dân', aliases: ['ncb', 'quocdan', 'navibank'] },
    { bin: '970448', name: 'OCB', fullName: 'NH TMCP Phương Đông', aliases: ['ocb', 'phuongdong'] },
    { bin: '970414', name: 'OceanBank', fullName: 'NH Thương Mại TNHH MTV Đại Dương', aliases: ['oceanbank', 'ocean', 'daiduong'] },
    { bin: '970430', name: 'PGBank', fullName: 'NH TMCP Thịnh Vượng và Phát Triển', aliases: ['pgbank', 'pgb', 'xangdau'] },
    { bin: '970439', name: 'PublicBank', fullName: 'NH TNHH MTV Public Việt Nam', aliases: ['publicbank', 'public', 'pbvn'] },
    { bin: '970412', name: 'PVcomBank', fullName: 'NH TMCP Đại Chúng Việt Nam', aliases: ['pvcombank', 'pvcom', 'daichungvietnam'] },
    { bin: '970400', name: 'SaigonBank', fullName: 'NH TMCP Sài Gòn Công Thương', aliases: ['saigonbank', 'sgb', 'saigoncongthuong'] },
    { bin: '970429', name: 'SCB', fullName: 'NH TMCP Sài Gòn', aliases: ['scb', 'saigon', 'saigonthuongtinbank'] },
    { bin: '970440', name: 'SeABank', fullName: 'NH TMCP Đông Nam Á', aliases: ['seabank', 'dongnama'] },
    { bin: '970443', name: 'SHB', fullName: 'NH TMCP Sài Gòn - Hà Nội', aliases: ['shb', 'saigonhanoi'] },
    { bin: '970424', name: 'Shinhan', fullName: 'NH TNHH MTV Shinhan Việt Nam', aliases: ['shinhan', 'shinhanbank'] },
    { bin: '970410', name: 'Standard Chartered', fullName: 'NH TNHH MTV Standard Chartered Việt Nam', aliases: ['standardchartered', 'scbvl', 'standardcharteredbank'] },
    { bin: '963388', name: 'Timo', fullName: 'Timo by BVBank', aliases: ['timo', 'timobank'] },
    { bin: '546035', name: 'Ubank', fullName: 'Ubank by VPBank', aliases: ['ubank', 'ubankbyvpbank'] },
    { bin: '970458', name: 'UOB', fullName: 'NH TNHH MTV United Overseas Bank Việt Nam', aliases: ['uob', 'uobvietnam', 'unitedoverseasbank'] },
    { bin: '970433', name: 'VietBank', fullName: 'NH TMCP Việt Nam Thương Tín', aliases: ['vietbank', 'vietnamthuongtin'] },
    { bin: '970427', name: 'VietABank', fullName: 'NH TMCP Việt Á', aliases: ['vietabank', 'vieta'] },
    { bin: '970441', name: 'VIB', fullName: 'NH TMCP Quốc Tế Việt Nam', aliases: ['vib', 'quocte'] },
    { bin: '971005', name: 'Viettel Money', fullName: 'Tổng Công ty Dịch vụ số Viettel', aliases: ['viettelmoney', 'viettelpay'] },
    { bin: '971011', name: 'VNPT Money', fullName: 'Tổng Công ty Dịch vụ số VNPT', aliases: ['vnptmoney', 'vnptpay'] },
    { bin: '970421', name: 'VRB', fullName: 'NH Liên Doanh Việt - Nga', aliases: ['vrb', 'vietnga', 'vietrussia'] },
    { bin: '970457', name: 'Woori', fullName: 'NH TNHH MTV Woori Việt Nam', aliases: ['woori', 'wooribank'] },
];

/** Strip diacritics and punctuation, lowercase: "Ngân hàng TP Bank" -> "tpbank". */
const normalizeBankName = (raw: string) =>
    raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/gi, 'd')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .replace(/^nganhang/, '')
        .replace(/^(thuongmaicophan|tmcp)/, '');

/** The bank recognised from `bankName`, or null when no QR can be built. */
export const findVietQrBank = (bankName: string | undefined) => {
    if (!bankName) return null;
    const key = normalizeBankName(bankName);
    return BANKS.find(b => normalizeBankName(b.name) === key || b.aliases.includes(key)) ?? null;
};

/** One bank as the picker shows it. */
export type VietQrBank = { bin: string; name: string; fullName: string };

/**
 * The banks the profile's bank picker offers. The picker writes `name` straight
 * into `bankInfo.bankName`, so every saved profile resolves to a BIN and the
 * transfer QR can always be built — which is the whole reason the field stopped
 * being free text.
 */
export const VIETQR_BANKS: VietQrBank[] = BANKS.map(({ bin, name, fullName }) => ({ bin, name, fullName }));

/**
 * The banks matching what was typed into the picker's search box. Matching is
 * on the normalised forms, so "tp bank", "TPB" and "tiên phong" all find
 * TPBank, and a substring is enough because people type the start of a name.
 */
export const searchVietQrBanks = (query: string): VietQrBank[] => {
    const key = normalizeBankName(query);
    if (!key) return VIETQR_BANKS;
    return BANKS
        .filter(b =>
            normalizeBankName(b.name).includes(key)
            || normalizeBankName(b.fullName).includes(key)
            || b.aliases.some(alias => alias.includes(key))
            || b.bin.includes(key))
        .map(({ bin, name, fullName }) => ({ bin, name, fullName }));
};

/**
 * Transfer note: no diacritics, letters/digits/spaces only, 25 characters max.
 * Many banking apps truncate or mangle accented text, and a long string loses
 * its tail. Dots get replaced too, so they are turned into spaces — wherever the
 * note is printed, print this exact string so anyone typing it by hand types the
 * same thing the QR carries.
 */
const NOTE_MAX = 25;

/** The character rules above, without the length cut. */
const normalizeNoteText = (text: string) =>
    text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/gi, 'd')
        .toUpperCase()
        .replace(/[^A-Z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

/** Both rules applied: what actually goes into a QR, and what gets printed. */
export const toTransferNote = (text: string) =>
    normalizeNoteText(text).slice(0, NOTE_MAX).trim();

/**
 * "<payer> <first words of the event>" — the recipient collects from several
 * people across several events, so the payer's name alone does not say which
 * bill a transfer settles. The event name is trimmed word by word so the note
 * never ends mid-word after the 25-character cut, and the payer's name is kept
 * whole because that is the part the recipient reconciles against.
 */
export const buildTransferNote = (payerName: string, eventName?: string) => {
    // Whole words only: a note cut mid-word ("NGUYEN VAN A RAT LA DAI D") reads
    // as a typo to whoever reconciles the transfers.
    const fit = (base: string, extra: string) => {
        let note = base;
        for (const word of extra.split(' ').filter(Boolean)) {
            const next = note ? `${note} ${word}` : word;
            if (next.length > NOTE_MAX) break;
            note = next;
        }
        return note;
    };

    const payer = normalizeNoteText(payerName);
    // A single word longer than the whole limit has no boundary to fall back on.
    const head = fit('', payer) || toTransferNote(payer);
    return fit(head, normalizeNoteText(eventName ?? ''));
};

const field = (id: string, value: string) => id + String(value.length).padStart(2, '0') + value;

// CRC-16/CCITT-FALSE: polynomial 0x1021, init 0xFFFF — the variant EMVCo requires.
const crc16 = (text: string) => {
    let crc = 0xffff;
    for (let i = 0; i < text.length; i++) {
        crc ^= text.charCodeAt(i) << 8;
        for (let bit = 0; bit < 8; bit++) {
            crc = crc & 0x8000 ? (crc << 1) ^ 0x1021 : crc << 1;
            crc &= 0xffff;
        }
    }
    return crc.toString(16).toUpperCase().padStart(4, '0');
};

/**
 * The VietQR string. `amount` > 0 makes it a one-off code with the amount baked
 * in (01=12), otherwise a static code the payer types the amount into (01=11).
 */
export const buildVietQrPayload = (bin: string, accountNumber: string, amount?: number, note?: string) => {
    const account = accountNumber.replace(/\s/g, '');
    const money = amount && amount > 0 ? String(Math.round(amount)) : '';
    const merchant = field('00', 'A000000727')
        + field('01', field('00', bin) + field('01', account))
        + field('02', 'QRIBFTTA');
    const body = field('00', '01')
        + field('01', money ? '12' : '11')
        + field('38', merchant)
        + field('53', '704')
        + (money ? field('54', money) : '')
        + field('58', 'VN')
        + (note ? field('62', field('08', note)) : '')
        + '6304';
    return body + crc16(body);
};

const makeQr = (text: string) => {
    const qr = qrcode(0, 'M');
    qr.addData(text, 'Byte');
    qr.make();
    return qr;
};

/** Render the string as an SVG data URL — crisp at any size, nothing to load. */
export const qrSvgDataUrl = (text: string) => {
    const qr = makeQr(text);
    const count = qr.getModuleCount();
    const margin = 4;
    const size = count + margin * 2;
    let path = '';
    for (let r = 0; r < count; r++) {
        for (let c = 0; c < count; c++) {
            if (qr.isDark(r, c)) path += `M${c + margin} ${r + margin}h1v1h-1z`;
        }
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">`
        + `<rect width="${size}" height="${size}" fill="#fff"/><path d="${path}" fill="#000"/></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

/** Transfer QR for one account, or null when the bank is not recognised. */
export const buildTransferQr = (bank: BankInfo, amount?: number, note?: string) => {
    const info = findVietQrBank(bank.bankName);
    if (!info || !/^\d{6,19}$/.test(bank.accountNumber.replace(/\s/g, ''))) return null;
    const cleanNote = note ? toTransferNote(note) : '';
    const payload = buildVietQrPayload(info.bin, bank.accountNumber, amount, cleanNote);
    return { payload, dataUrl: qrSvgDataUrl(payload), bankShortName: info.name, note: cleanNote };
};

/**
 * A PNG the payer SAVES TO THEIR PHONE and then opens inside their banking app
 * ("Scan QR" -> pick an image) — the only route that works with every bank when
 * the phone showing the code is the phone paying. PNG rather than SVG because
 * photo libraries and banking apps do not read SVG. A few lines of text are
 * drawn under the code so the image still explains itself weeks later.
 */
export const transferQrPng = (payload: string, lines: string[]): Promise<Blob> => {
    const qr = makeQr(payload);
    const count = qr.getModuleCount();
    const cell = 10;
    const margin = 4 * cell;
    const qrSize = count * cell + margin * 2;
    const lineHeight = 40;
    const width = Math.max(qrSize, 600);
    const height = qrSize + lines.length * lineHeight + 40;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return Promise.reject(new Error('Canvas unavailable'));

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#000';
    const left = (width - qrSize) / 2 + margin;
    for (let r = 0; r < count; r++) {
        for (let c = 0; c < count; c++) {
            if (qr.isDark(r, c)) ctx.fillRect(left + c * cell, margin + r * cell, cell, cell);
        }
    }

    ctx.textAlign = 'center';
    ctx.fillStyle = '#1e293b';
    lines.forEach((line, i) => {
        ctx.font = `${i === 0 ? 'bold ' : ''}28px -apple-system, "Segoe UI", Roboto, sans-serif`;
        ctx.fillText(line, width / 2, qrSize + 10 + (i + 0.7) * lineHeight);
    });

    return new Promise((resolve, reject) => canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error('Could not draw image'))),
        'image/png',
    ));
};
